import { matchPath } from 'react-router-dom'
import { Routes, SEO_META_CONFIG } from './routes'

/**
 * Routes are matched in this explicit order so SEO resolution does not inherit
 * the accidental first-match behavior of the generic route table.
 *
 * @type {string[]}
 */
export const EXPLICIT_META_ROUTE_ORDER = [
  'userpromotionpageId',
  'promotionpageId',
  'promotionpage',
  'sportsbook',
  'loyaltyBonusPage',
  'calendar',
  'VIP',
  'home',
  'homepage',
  'login',
  'signup',
  'forgotPassword',
  'resetPassword',
  'resetEmail',
  'resendEmail',
  'notFound',
  'forbidden',
  'siteMaintenance',
  'siteMaintenanceStatic',
  'blockedCountry',
  'emailTokenLink',
  'paymentSuccess',
  'paymentStatus',
  'deposit',
  'withdraw',
  'accountInfo',
  'bonusAccount',
  'transactionHistory',
  'account',
  'playGame',
  'mobilePlayGame',
  'demoGame',
  'wpAdmin',
  'CashbackBonus'
]

const CMS_ROUTE_TYPE = 'cms'
const CASINO_ROUTE_TYPE = 'casinoSubCategory'
const PROMOTION_DETAIL_ROUTE_TYPE = 'promotionDetail'
const STATIC_ROUTE_TYPE = 'static'
const DEFAULT_ROUTE_TYPE = 'default'

/**
 * Returns a strict route match for a pathname/pattern pair.
 *
 * @param {string} pathname - Current browser pathname.
 * @param {string} routePattern - Route definition to match against.
 * @returns {import('react-router-dom').PathMatch<string>|null} Route match result.
 */
const getRouteMatch = (pathname, routePattern) => {
  if (!pathname || !routePattern) return null
  return matchPath({ path: routePattern, end: true }, pathname)
}

/**
 * Resolves a localized CMS field that may either be a plain string or a
 * language-keyed object.
 *
 * @param {string|Object<string, string>|undefined|null} value - CMS value to resolve.
 * @param {string} selectedLanguageCode - Active language code.
 * @returns {string|null} Best available localized value.
 */
const getCmsMetaValue = (value, selectedLanguageCode) => {
  if (!value) return null

  if (typeof value === 'string') {
    return value
  }

  return value?.[selectedLanguageCode] || value?.EN || null
}

/**
 * Converts base SEO keys into field-specific keys.
 *
 * @param {Array<string|null|undefined>} baseKeys - SEO key prefixes like `seo_casino`.
 * @param {'title'|'description'} field - Meta field suffix.
 * @returns {string[]} Concrete `languageData` lookup keys.
 */
const buildSeoFieldKeys = (baseKeys, field) => {
  return baseKeys.filter(Boolean).map(baseKey => `${baseKey}_${field}`)
}

/**
 * Collects likely slug candidates from the promotion detail payload. The API
 * contract is not fixed yet, so the lookup stays defensive.
 *
 * @param {Object} promotionDetail - Promotion detail object from Redux.
 * @returns {Array<string|Object<string, string>|undefined>} Possible slug values.
 */
const getPromotionSlugCandidates = (promotionDetail) => {
  return [
    promotionDetail?.slug,
    promotionDetail?.promotionSlug,
    promotionDetail?.bonusSlug,
    promotionDetail?.bonus?.slug,
    promotionDetail?.bonus?.promotionSlug,
    promotionDetail?.bonus?.bonusSlug,
    promotionDetail?.other?.slug,
    promotionDetail?.other?.promotionSlug,
    promotionDetail?.other?.bonusSlug
  ]
}

/**
 * Normalizes a route segment so it can be used as part of a `languageData` key.
 *
 * @param {string|number|undefined|null} value - Raw route segment or identifier.
 * @returns {string} Normalized value using lowercase snake_case.
 */
export const normalizeMetaSegment = (value) => {
  if (!value) return ''

  let decodedValue = String(value).trim()

  try {
    decodedValue = decodeURIComponent(decodedValue)
  } catch (error) {
    decodedValue = String(value).trim()
  }

  return decodedValue
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Classifies the current pathname into a meta-tag route context. Promotion
 * details and static routes are matched before generic casino/CMS routes.
 *
 * @param {string} pathname - Current browser pathname.
 * @param {Object<string, string>} [routes=Routes] - Route config to classify against.
 * @returns {{routeKey: string|null, routeType: string, params: Object<string, string>}} Route context.
 */
export const getMetaRouteContext = (pathname, routes = Routes) => {
  // Match the explicitly ordered list first so specific routes do not fall
  // through to the broader casino or CMS patterns.
  for (const routeKey of EXPLICIT_META_ROUTE_ORDER) {
    const match = getRouteMatch(pathname, routes[routeKey])

    if (match) {
      const routeType = ['promotionpageId', 'userpromotionpageId'].includes(routeKey)
        ? PROMOTION_DETAIL_ROUTE_TYPE
        : STATIC_ROUTE_TYPE

      return {
        routeKey,
        routeType,
        params: match.params || {}
      }
    }
  }

  const casinoMatch = getRouteMatch(pathname, routes.casinoSubCategory)
  if (casinoMatch) {
    return {
      routeKey: CASINO_ROUTE_TYPE,
      routeType: CASINO_ROUTE_TYPE,
      params: casinoMatch.params || {}
    }
  }

  const cmsMatch = getRouteMatch(pathname, routes.cms)
  if (cmsMatch) {
    return {
      routeKey: CMS_ROUTE_TYPE,
      routeType: CMS_ROUTE_TYPE,
      params: cmsMatch.params || {}
    }
  }

  return {
    routeKey: null,
    routeType: DEFAULT_ROUTE_TYPE,
    params: {}
  }
}

/**
 * Resolves the canonical promotion slug from promotion detail data.
 *
 * @param {Object} promotionDetail - Promotion detail object from Redux.
 * @param {string} selectedLanguageCode - Active language code.
 * @returns {string|undefined} First non-empty slug candidate.
 */
export const getPromotionSlugFromDetail = (promotionDetail, selectedLanguageCode) => {
  return getPromotionSlugCandidates(promotionDetail)
    .map(candidate => getCmsMetaValue(candidate, selectedLanguageCode))
    .find(candidate => typeof candidate === 'string' && candidate.trim())
}

/**
 * Finds the promotion detail entry that corresponds to the current route.
 * It first tries direct Redux indexing by ids, then falls back to slug lookup
 * so slug-based promotion URLs can reuse data loaded under a numeric bonus id.
 *
 * @param {Object} params - Promotion detail lookup inputs.
 * @param {string|number} params.routeIdentifier - Identifier from `/promotion/:identifier`.
 * @param {string|number} [params.userBonusId] - Optional user bonus route param.
 * @param {Object<string, Object>} [params.bonusDetailsById={}] - Promotion details keyed by bonus id.
 * @param {Object<string, Object>} [params.userBonusDetailsById={}] - Promotion details keyed by user bonus id.
 * @param {string} params.selectedLanguageCode - Active language code.
 * @returns {Object|null} Matching promotion detail object, if found.
 */
export const findPromotionDetail = ({
  routeIdentifier,
  userBonusId,
  bonusDetailsById = {},
  userBonusDetailsById = {},
  selectedLanguageCode
}) => {
  if (userBonusId && userBonusDetailsById?.[userBonusId]) {
    return userBonusDetailsById[userBonusId]
  }

  if (routeIdentifier && bonusDetailsById?.[routeIdentifier]) {
    return bonusDetailsById[routeIdentifier]
  }

  const normalizedRouteIdentifier = normalizeMetaSegment(routeIdentifier)
  const detailPool = [
    ...Object.values(bonusDetailsById || {}),
    ...Object.values(userBonusDetailsById || {})
  ]

  return detailPool.find((detail) => {
    const slug = getPromotionSlugFromDetail(detail, selectedLanguageCode)
    const normalizedSlug = normalizeMetaSegment(slug)

    return normalizedSlug === normalizedRouteIdentifier ||
    String(detail?.bonusId || '') === String(routeIdentifier || '')
  }) || null
}

/**
 * Builds fallback SEO base keys for casino sub-category routes.
 *
 * @param {Object} params - Casino route params.
 * @param {string} params.casinoCategory - Casino category route param.
 * @param {string} params.casinoSubCategory - Casino sub-category route param.
 * @returns {string[]} Ordered casino SEO base keys.
 */
export const getCasinoMetaBaseKeys = ({ casinoCategory, casinoSubCategory }) => {
  const normalizedCategory = normalizeMetaSegment(casinoCategory)
  const normalizedSubCategory = normalizeMetaSegment(casinoSubCategory)

  return [
    normalizedCategory && normalizedSubCategory
      ? `seo_${normalizedCategory}_${normalizedSubCategory}`
      : null,
    'seo_casino',
    'seo_default'
  ]
}

/**
 * Builds fallback SEO base keys for promotion detail routes.
 *
 * @param {Object} params - Promotion key inputs.
 * @param {string|number} params.routeIdentifier - Identifier from the URL.
 * @param {string} [params.resolvedSlug] - Canonical slug from promotion detail data.
 * @returns {string[]} Ordered promotion SEO base keys.
 */
export const getPromotionMetaBaseKeys = ({ routeIdentifier, resolvedSlug }) => {
  const normalizedSlug = normalizeMetaSegment(resolvedSlug)
  const normalizedRouteIdentifier = normalizeMetaSegment(routeIdentifier)

  return [
    normalizedSlug ? `seo_promotion_${normalizedSlug}` : null,
    normalizedRouteIdentifier ? `seo_promotion_${normalizedRouteIdentifier}` : null,
    'seo_promotion',
    'seo_default'
  ]
}

/**
 * Builds fallback SEO base keys for static routes backed by `SEO_META_CONFIG`.
 *
 * @param {Object} params - Static route key inputs.
 * @param {string|null} params.routeKey - Matched route key.
 * @param {Object<string, string>} [params.seoMetaConfig=SEO_META_CONFIG] - Static route SEO mapping.
 * @returns {string[]} Ordered static SEO base keys.
 */
export const getStaticMetaBaseKeys = ({ routeKey, seoMetaConfig = SEO_META_CONFIG }) => {
  const seoKey = seoMetaConfig?.[routeKey]

  if (seoKey) {
    return [`seo_${seoKey}`, 'seo_default']
  }

  return ['seo_default']
}

/**
 * Resolves a meta value by checking `languageData` for the first existing key.
 *
 * @param {Object} params - Meta resolution inputs.
 * @param {Array<string|null|undefined>} params.baseKeys - Ordered SEO key prefixes.
 * @param {'title'|'description'} params.field - Field to resolve.
 * @param {Object<string, string>} params.languageData - Current translation payload.
 * @param {string} params.fallbackValue - Final fallback value.
 * @returns {string} Resolved meta value.
 */
export const resolveMetaValue = ({ baseKeys, field, languageData, fallbackValue }) => {
  const keys = buildSeoFieldKeys(baseKeys, field)

  for (const key of keys) {
    if (languageData?.[key]) {
      return languageData[key]
    }
  }

  return fallbackValue
}

/**
 * Resolves the final title/description pair for the current route.
 *
 * @param {Object} params - Meta resolution inputs.
 * @param {string} params.pathname - Current browser pathname.
 * @param {Object<string, string>} params.languageData - Current translation payload.
 * @param {string} params.selectedLanguageCode - Active language code.
 * @param {Object} params.cmsDetails - CMS detail object for CMS routes.
 * @param {Object|null} params.promotionDetail - Promotion detail object for promotion routes.
 * @param {string} params.defaultTitle - Final title fallback.
 * @param {string} params.defaultDescription - Final description fallback.
 * @param {Object<string, string>} [params.routes=Routes] - Route config used for classification.
 * @param {Object<string, string>} [params.seoMetaConfig=SEO_META_CONFIG] - Static route SEO mapping.
 * @returns {{title: string, description: string, routeContext: {routeKey: string|null, routeType: string, params: Object<string, string>}}}
 * Resolved title, description and route context.
 */
export const resolveMetaTags = ({
  pathname,
  languageData,
  selectedLanguageCode,
  cmsDetails,
  promotionDetail,
  defaultTitle,
  defaultDescription,
  routes = Routes,
  seoMetaConfig = SEO_META_CONFIG
}) => {
  const routeContext = getMetaRouteContext(pathname, routes)

  if (routeContext.routeType === CMS_ROUTE_TYPE) {
    return {
      title: getCmsMetaValue(cmsDetails?.metaTitle, selectedLanguageCode) ||
        getCmsMetaValue(cmsDetails?.title, selectedLanguageCode) ||
        languageData?.seo_default_title ||
        defaultTitle,
      description: getCmsMetaValue(cmsDetails?.metaDescription, selectedLanguageCode) ||
        getCmsMetaValue(cmsDetails?.title, selectedLanguageCode) ||
        languageData?.seo_default_description ||
        defaultDescription,
      routeContext
    }
  }

  if (routeContext.routeType === CASINO_ROUTE_TYPE) {
    const baseKeys = getCasinoMetaBaseKeys(routeContext.params)

    return {
      title: resolveMetaValue({
        baseKeys,
        field: 'title',
        languageData,
        fallbackValue: defaultTitle
      }),
      description: resolveMetaValue({
        baseKeys,
        field: 'description',
        languageData,
        fallbackValue: defaultDescription
      }),
      routeContext
    }
  }

  if (routeContext.routeType === PROMOTION_DETAIL_ROUTE_TYPE) {
    // Promotion details can start on a numeric id route and later resolve to a
    // canonical slug once the detail API returns. Prefer the slug when present.
    const resolvedSlug = getPromotionSlugFromDetail(promotionDetail, selectedLanguageCode)
    const routeIdentifier = routeContext.params?.bonusId
    const baseKeys = getPromotionMetaBaseKeys({
      routeIdentifier,
      resolvedSlug
    })

    return {
      title: resolveMetaValue({
        baseKeys,
        field: 'title',
        languageData,
        fallbackValue: defaultTitle
      }),
      description: resolveMetaValue({
        baseKeys,
        field: 'description',
        languageData,
        fallbackValue: defaultDescription
      }),
      routeContext
    }
  }

  const baseKeys = getStaticMetaBaseKeys({
    routeKey: routeContext.routeKey,
    seoMetaConfig
  })

  return {
    title: resolveMetaValue({
      baseKeys,
      field: 'title',
      languageData,
      fallbackValue: defaultTitle
    }),
    description: resolveMetaValue({
      baseKeys,
      field: 'description',
      languageData,
      fallbackValue: defaultDescription
    }),
    routeContext
  }
}
